import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Box, Button, CircularProgress, Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { MarkEmailRead, PersonAddAlt, PriorityHigh, RemoveRedEye } from "@mui/icons-material";
import { format } from "date-fns";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import { NotificationContext } from "src/components/providers/notification";
import { useAuth } from "src/components/providers/auth-context";
import Icon from "src/components/icon";
import slackIcon from "src/assets/icons/slack-int";
import gitlabIcon from "src/assets/icons/gitlab-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import githubIcon from "src/assets/icons/github-int";
import googleWorkspaceInt from "src/assets/icons/google-workspace-int";
import { Helmet } from "react-helmet";
import missionSuccessfulIcon from "src/assets/icons/mission-successful";
import missionFailedIcon from "src/assets/icons/mission-failed";
import jiraIcon from "src/assets/icons/jira-int";
import notionIcon from "src/assets/icons/notion-int";
import Link from "src/components/link";
import microsoftCalendarIcon from "src/assets/icons/microsoft-calendar-int";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import request from "../../utils/request";

const NotificationItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding: 12px;
    width: 100%;
`;

const NotificationIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 46px;
    height: 46px;
    background-color: #3d3d3d;
    border-radius: 50%;
    margin-right: 16px;
`;

const NotificationContentWrapper = styled.div`
    word-break: break-word;
`;

const NotificationSubContentWrapper = styled.div`
    word-break: break-word;
    font-size: 11px;
    opacity: 0.8;
`;

const FlexBox = styled.div`
    display: flex;
    align-items: center;
`;

const ErrorText = styled.p`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 8px;
    padding: 0;
    text-align: center;
`;

const DateText = styled.span`
    font-size: 10px;
    opacity: 0.5;
`;

const LoadButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 12px;
    width: 100%;
`;

const LoadMore = styled(Button)`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const Strong = styled.span`
    font-weight: 600;
`;

const NotificationIcon = ({ type = "", data }) => {
    const assignIcon = () => {
        if (type === "alert") {
            return <PriorityHigh />;
        }
        if (type === "add") {
            return <PersonAddAlt />;
        }
        if (type === "mission") {
            if (data.missionStatus === "completed") {
                return <Icon svg={missionSuccessfulIcon} />;
            }
            if (data.missionStatus === "failed") {
                return <Icon svg={missionFailedIcon} />;
            }
        }
        if (type === "integration") {
            if (data.integration === "slack") {
                return <Icon svg={slackIcon} />;
            }
            if (data.integration === "gitlab") {
                return <Icon svg={gitlabIcon} />;
            }
            if (data.integration === "gitlabSelfManaged") {
                return <Icon svg={gitlabSelfManagedIcon} />;
            }
            if (data.integration === "github") {
                return <Icon svg={githubIcon} />;
            }
            if (data.integration === "google") {
                return <Icon svg={googleWorkspaceInt} />;
            }
            if (data.integration === "jira") {
                return <Icon svg={jiraIcon} />;
            }
            if (data.integration === "microsoft") {
                return <Icon svg={microsoftCalendarIcon} />;
            }
            if (data.integration === "notion") {
                return <Icon svg={notionIcon} />;
            }
        }
        if (type === "custom-causal-graph") {
            return <AccountTreeIcon />;
        }
        if (type === "achievement") {
            const isProd = process.env.ENVIRONMENT_NAME === "prod";
            const setAssetLink = () => {
                if (isProd) return "https://assets.adadot.com/achievements";
                return "https://assets-sit.adadot.com/achievements";
            };
            const assetLink = setAssetLink();
            return (
                <Box
                    component="img"
                    sx={{ width: "36px", borderRadius: "40px" }}
                    alt={data.name}
                    src={`${assetLink}/${data.achievementCategory}/${data.achievementId}.png`}
                />
            );
        }
        return <PriorityHigh />;
    };
    const iconToShow = assignIcon();
    return <NotificationIconWrapper>{iconToShow}</NotificationIconWrapper>;
};

const NotificationContent = ({ type, data }) => {
    if (type === "integration") {
        if (data.integrationStatus === "completed")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has finished scrapping
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" || data.integration === "google") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "limited-data")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has some data!
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" ||
                        data.integration === "google" ||
                        data.integration === "microsoft") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "scrapping")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has started scrapping!
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            soon!
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" ||
                        data.integration === "google" ||
                        data.integration === "microsoft") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections soon!
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section soon!
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section soon!
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "inactive")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        is inactive
                    </NotificationContentWrapper>
                    <NotificationSubContentWrapper>{data.details}</NotificationSubContentWrapper>
                </Stack>
            );
    }
    if (type === "add") {
        return (
            <NotificationContentWrapper>
                <Strong>{data.user}</Strong> has added you to <Strong>{data.team}</Strong>
            </NotificationContentWrapper>
        );
    }
    if (type === "custom-causal-graph") {
        return (
            <Stack direction="column">
                <NotificationContentWrapper>
                    Your <Strong> personalised Causal AI model</Strong> has been calculated.
                </NotificationContentWrapper>
                <NotificationSubContentWrapper>{data.additionalInfo}</NotificationSubContentWrapper>
            </Stack>
        );
    }
    if (type === "achievement") {
        return (
            <NotificationContentWrapper>
                <Link variant="plain" to="/achievements">
                    New achievement <Strong>{data.achievementName}</Strong>
                </Link>
            </NotificationContentWrapper>
        );
    }
    if (type === "mission") {
        if (data && !data.userId) {
            return (
                <NotificationContentWrapper>
                    <Link variant="plain" to={`/teams/${data.teamId}`}>
                        Team {data.teamName} {data.missionStatus}{" "}
                        <Strong>{data.missionName}</Strong>
                    </Link>
                </NotificationContentWrapper>
            );
        }
        return (
            <NotificationContentWrapper>
                <Link variant="plain" to={`/teams/${data.teamId}`}>
                    Mission <Strong>{data.missionName}</Strong> {data.missionStatus}
                </Link>
            </NotificationContentWrapper>
        );
    }
    return <div>No Content</div>;
};

const NotificationItem = ({ type, data, createdAt, status, onClose }) => {
    return (
        <NotificationItemWrapper>
            <FlexBox>
                <NotificationIcon type={type} data={data} />
                <Stack direction="column">
                    <NotificationContent type={type} data={data} />
                    <Tooltip title={format(new Date(createdAt), "PP - ppp")}>
                        <DateText>{formatDistanceToNow(createdAt, { addSuffix: true })}</DateText>
                    </Tooltip>
                </Stack>
            </FlexBox>
            {status === "unread" && (
                <IconButton size="medium" onClick={onClose}>
                    <RemoveRedEye />
                </IconButton>
            )}
        </NotificationItemWrapper>
    );
};

const Notifications = () => {
    const { removeNotification } = useContext(NotificationContext);
    const { user } = useAuth();
    const [allNotifications, setNotifications] = useState([]);
    const [lastKey, setLastKey] = useState(undefined);
    const [firstLoading, setFirstLoading] = useState(true);
    const limit = 10;

    const fetchNotifications = async () => {
        const data = await request("/notifications-api/retrieve", {
            method: "POST",
            body: JSON.stringify({
                userId: user?.id,
                limit,
                lastKey,
            }),
        });
        setLastKey(data?.LastEvaluatedKey);
        setNotifications([...allNotifications, ...data.Items]);
        setFirstLoading(false);
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const handleDeleteItem = async (item) => {
        // TODO add error handling with toast
        await request(`notifications-api/update`, {
            method: "POST",
            body: JSON.stringify({ id: item.id }),
        });
        removeNotification(item);
        setNotifications((previousNotifications) =>
            previousNotifications.map((notification) => ({
                ...notification,
                status: notification.id === item.id ? "read" : notification.status,
            }))
        );
    };

    const renderNotifications = () => {
        if (firstLoading) return <CircularProgress />;
        if (!allNotifications || !allNotifications.length)
            return (
                <Stack
                    direction="column"
                    alignItems="center"
                    mt={2.5}
                    mb={2}
                    style={{ opacity: "0.7" }}
                >
                    <MarkEmailRead />
                    <ErrorText>You have no unread notifications</ErrorText>
                </Stack>
            );
        return (
            <>
                {allNotifications.map((notification) => (
                    <NotificationItem
                        key={notification.id}
                        type={notification.type}
                        status={notification.status}
                        data={notification.data}
                        createdAt={notification.createdAt}
                        onClose={() => handleDeleteItem(notification)}
                    />
                ))}

                {lastKey && (
                    <LoadButtonWrapper>
                        <LoadMore onClick={() => fetchNotifications()}>More Notifications</LoadMore>
                    </LoadButtonWrapper>
                )}
            </>
        );
    };

    return (
        <>
            <Helmet>
                <title>Adadot - Notifications</title>
            </Helmet>
            <Box width="100%" maxWidth="576px" marginTop="4vh" marginBottom="4vh">
                <Grid
                    container
                    spacing={5}
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                >
                    <>{renderNotifications()}</>
                </Grid>
            </Box>
        </>
    );
};

export default Notifications;
